/**
 * postMessage protocol between Joyia Web (the host shell) and an app it embeds in an
 * iframe (KnowledgeFlow today; Deploy, Control Center, ... next).
 *
 * Paths on the wire are always APP paths (`pathname + search` inside the iframe). The host
 * owns the translation to its own URL space, so an embedded app never needs to know where
 * it is mounted.
 *
 * Every receiver MUST verify both `event.origin` and `event.source`. Authentication
 * messages are only for explicitly trusted first-party apps, never arbitrary frames.
 */

export type EmbedTheme = "light" | "dark";

/**
 * `theme-change` keeps its historical name: Tribes' ThemeProvider already listens for it,
 * so renaming would silently break that consumer.
 */
export type EmbedMessage =
  | { type: "joyia:embed:navigate"; path: string } // host → app
  | { type: "joyia:embed:route"; path: string } // app → host
  | { type: "theme-change"; theme: EmbedTheme } // host → app
  | { type: "joyia:embed:request-theme" } // app → host
  | { type: "joyia:embed:auth-request"; requestId: string; force: boolean } // app → host
  | { type: "joyia:embed:auth-response"; requestId: string; token: string | null } // host → app
  | { type: "joyia:embed:logout" } // either direction
  // app → host: the user's server-side preferences (persona, theme, language) changed, so the
  // host must refetch /me instead of serving its cached copy.
  | { type: "joyia:embed:preferences-changed" };

/** Narrows untrusted `event.data`. Returns null for anything that is not a well-formed message. */
export function parseEmbedMessage(data: unknown): EmbedMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const msg = data as Record<string, unknown>;
  switch (msg.type) {
    case "joyia:embed:navigate":
    case "joyia:embed:route":
      // App-relative only: an absolute or protocol-relative (`//host`) path could steer a
      // router off-app.
      return typeof msg.path === "string" && msg.path.startsWith("/") && !msg.path.startsWith("//")
        ? { type: msg.type, path: msg.path }
        : null;
    case "theme-change":
      return msg.theme === "light" || msg.theme === "dark"
        ? { type: "theme-change", theme: msg.theme }
        : null;
    case "joyia:embed:request-theme":
      return { type: "joyia:embed:request-theme" };
    case "joyia:embed:auth-request":
      return typeof msg.requestId === "string" && msg.requestId.length > 0 && typeof msg.force === "boolean"
        ? { type: msg.type, requestId: msg.requestId, force: msg.force }
        : null;
    case "joyia:embed:auth-response":
      return typeof msg.requestId === "string" && msg.requestId.length > 0 &&
        (msg.token === null || (typeof msg.token === "string" && msg.token.length > 0))
        ? { type: msg.type, requestId: msg.requestId, token: msg.token }
        : null;
    case "joyia:embed:logout":
      return { type: msg.type };
    case "joyia:embed:preferences-changed":
      return { type: "joyia:embed:preferences-changed" };
    default:
      return null;
  }
}

/** `https://a.example/x/` → `https://a.example`; null for an empty or unparsable URL. */
export function originOf(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/** Query params the host appends to the iframe's initial URL. */
export const EMBED_PARAM = "embedded";
export const EMBED_THEME_PARAM = "theme";

/**
 * True when rendered inside the host shell. `?embedded=1` covers the first paint and the
 * case where `window.top` is unreadable; the frame check covers in-app navigations that
 * dropped the query string.
 */
export function isEmbedded(): boolean {
  if (typeof window === "undefined") return false;
  if (new URLSearchParams(window.location.search).get(EMBED_PARAM) === "1") return true;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Theme the host passed on the initial URL, if any. */
export function initialEmbedTheme(): EmbedTheme | undefined {
  if (typeof window === "undefined") return undefined;
  const theme = new URLSearchParams(window.location.search).get(EMBED_THEME_PARAM);
  return theme === "light" || theme === "dark" ? theme : undefined;
}
